import { Box, Paper, Stack, Typography, Divider } from '@mui/material';
import ResponsiveTooltip from '../ResponsiveTooltip';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { TaxReportSummary as TaxReportSummaryData, TaxReportSummaryGroup } from '../../services/tax-reports.types';
import { useAuth } from '../../context/AuthContext';
import SkeletonWithTooltip from './components/SkeletonWithTooltip';

// Template structure for tax reports
const TAX_REPORT_TEMPLATE = [
  {
    label: 'Steuerpflichtiges Einkommen',
    translationKey: 'taxReports.sections.taxable',
    items: [
      {
        label: 'Einkünfte aus privaten Veräußerungsgeschäften nach § 23 EStG',
        displayLabel: 'Gewinne aus dem Verkauf von Kryptowährungen',
        translationKey: 'taxReports.summary.Private sale income according to Art. 23 German income tax act'
      },
      {
        label: 'Sonstige Einkünfte nach § 22 Nr. 3 EStG',
        displayLabel: 'Sonstige Krypto-Einnahmen',
        translationKey: 'taxReports.summary.Other income according to Art. 22 No. 3 German income tax act'
      },
      {
        label: 'Kapitalerträge aus Margin- und Derivategeschäften',
        translationKey: 'taxReports.summary.Capital gains from margin and derivatives',
        calculateValue: (data: TaxReportSummaryData[]) => {
          const gains = findValueByLabel(data, 'Gewinne aus Margin, Derivate, Futures');
          const losses = findValueByLabel(data, 'Verluste aus Margin, Derivate, Futures');
          
          // Simple calculation: gains minus losses
          const netValue = gains - losses;
          
          return {
            value: netValue,
          };
        }
      }
    ]
  },
  {
    label: 'Weitere steuerrelevante Transaktionen',
    translationKey: 'taxReports.sections.other',
    items: [
      {
        label: 'Eingegangene Spenden und Schenkungen',
        displayLabel: 'Erhaltene Geschenke',
        displayText: 'Erhaltene Geschenke',
        translationKey: 'taxReports.summary.Incoming donations and gifts'
      },
      {
        label: 'Ausgegangene Spenden und Schenkungen',
        displayLabel: 'Gesendete Spenden und Geschenke',
        displayText: 'Gesendete Spenden und Geschenke',
        translationKey: 'taxReports.summary.Outgoing donations and gifts'
      },
      {
        label: 'Verlorene und gestohlene Coins',
        displayLabel: 'Verlorene oder gestohlene Assets',
        displayText: 'Verlorene oder gestohlene Assets',
        translationKey: 'taxReports.summary.Coins lost or stolen'
      }
    ]
  },
  {
    label: 'Steuerfreies Einkommen',
    translationKey: 'taxReports.sections.nonTaxable',
    items: [
      {
        label: 'Nicht steuerbare Einkünfte aus privaten Veräußerungsgeschäften',
        displayLabel: 'Gewinne aus dem Verkauf von Kryptowährungen',
        displayText: 'Gewinne aus dem Verkauf von Kryptowährungen',
        translationKey: 'taxReports.summary.Private sale income non taxable'
      },
      {
        label: 'Nicht steuerbare sonstige Einkünfte',
        displayLabel: 'Sonstige Krypto-Einnahmen',
        displayText: 'Sonstige Krypto-Einnahmen',
        translationKey: 'taxReports.summary.Other income non taxable',
        calculateValue: (data: TaxReportSummaryData[]) => {
          const nonTaxableOtherIncome = findValueByLabel(data, 'Nicht steuerbare sonstige Einkünfte');
          const mintingValue = findValueByLabel(data, 'Minting');
          
          // Combine the values
          const totalValue = nonTaxableOtherIncome + mintingValue;
          
          return {
            value: totalValue
          };
        }
      }
    ]
  }
];

// Get total amount from a group
const getTotalAmount = (group: TaxReportSummaryGroup): number => {
  return group.entries.reduce((total, entry) => {
    if (entry.kv) {
      return total + entry.kv.value;
    }
    if (entry.g) {
      return total + getTotalAmount(entry.g);
    }
    return total;
  }, 0);
};

// Helper function to normalize labels for comparison
const normalizeLabel = (label: string): string => {
  return label
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\u00e4/g, 'ä')
    .replace(/\u00fc/g, 'ü')
    .replace(/\u00f6/g, 'ö')
    .replace(/\u00df/g, 'ß')
    .replace(/\u00c4/g, 'Ä')
    .replace(/\u00dc/g, 'Ü')
    .replace(/\u00d6/g, 'Ö')
    .replace(/\u00e9/g, 'é')
    .replace(/\u00e8/g, 'è')
    .replace(/\u00ea/g, 'ê')
    .replace(/\u00e0/g, 'à')
    .replace(/\u00e2/g, 'â')
    .replace(/\u00f9/g, 'ù')
    .replace(/\u00fb/g, 'û')
    .replace(/\u00ee/g, 'î')
    .replace(/\u00ef/g, 'ï')
    .replace(/\u00e7/g, 'ç')
    .toLowerCase();
};

// Find a value in the data by label
const findValueByLabel = (data: TaxReportSummaryData[], label: string): number => {
  if (!data || data.length === 0) return 0;
  
  for (const section of data) {
    if (!section.g) continue;
    
    for (const entry of section.g.entries) {
      if (entry.kv && entry.kv.label === label) {
        return entry.kv.value;
      }
      
      // Try to match with normalized labels (for Unicode comparison)
      if (entry.kv && normalizeLabel(entry.kv.label) === normalizeLabel(label)) {
        return entry.kv.value;
      }
      
      if (entry.g) {
        for (const subEntry of entry.g.entries) {
          if (subEntry.kv && subEntry.kv.label === label) {
            return subEntry.kv.value;
          }
          
          // Try to match with normalized labels (for Unicode comparison)
          if (subEntry.kv && normalizeLabel(subEntry.kv.label) === normalizeLabel(label)) {
            return subEntry.kv.value;
          }
        }
      }
    }
  }
  
  return 0;
};

interface TaxReportSummaryProps {
  data: TaxReportSummaryData[];
  isPaid: boolean;
  onNavigate?: () => void;
}

const TaxReportSummary: React.FC<TaxReportSummaryProps> = ({ data, isPaid }) => {
  const { currentCtUser } = useAuth();
  const { t } = useTranslation();

  const formatAmount = (amount: number | undefined) => {
    if (amount === undefined || amount === null || isNaN(amount)) return `0.00 ${currentCtUser?.currency || ''}`;
    return `${amount.toFixed(2)} ${currentCtUser?.currency || ''}`;
  };

  const TaxSection = ({
    section,
  }: {
    section: typeof TAX_REPORT_TEMPLATE[0];
  }) => {
    return (
      <Paper 
        elevation={0} 
        sx={{
          p: { xs: 2, sm: 3 },
          mb: 1.5,
          borderRadius: 1,
        }}
      >
        <Box sx={{
          mb: 1,
        }}>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            mb: 1,
          }}>
            <Typography 
              variant="subtitle1" 
              sx={{
                fontWeight: 700,
                fontSize: '1.25rem',
              }}
            >
              {t(section.translationKey)}
            </Typography>
            {section.translationKey === 'taxReports.sections.other' && (
              <ResponsiveTooltip
                title={
                  <Box sx={{ p: 1.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '0.9rem' }}>
                      {t('taxReports.tooltips.otherTransactions')}
                    </Typography>
                  </Box>
                }
                arrow
                placement="top"
                componentsProps={{
                  tooltip: {
                    sx: {
                      p: 0,
                      bgcolor: (theme) => theme.palette.background.paper,
                      color: (theme) => theme.palette.text.primary,
                      boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                      border: '1px solid',
                      borderColor: (theme) => theme.palette.divider,
                      borderRadius: 1,
                      fontSize: '0.9rem',
                      fontWeight: 'bold',
                      '& .MuiTooltip-arrow': {
                        color: (theme) => theme.palette.background.paper,
                      }
                    },
                  },
                  popper: {
                    sx: {
                      '& .MuiTooltip-tooltip': {
                        p: 0,
                      }
                    }
                  }
                }}
              >
                <InfoOutlinedIcon sx={{ ml: 1, fontSize: '1.1rem', color: 'text.secondary' }} />
              </ResponsiveTooltip>
            )}
          </Box>
          <Divider sx={{ my: 0.75, height: '2px', opacity: 0.6 }} />
        </Box>
        <Box sx={{
          mb: 1,
        }}>
          <Stack spacing={1.5}>
            {renderItems(section.items)}
          </Stack>
        </Box>
      </Paper>
    );
  };

  const renderItems = (items: any[], level = 0) => {
    return items.map((item, index) => {
      if (item.subItems && item.subItems.length > 0) {
        return (
          <Box key={index}>
            <Typography 
              variant="body2" 
              sx={{
                mb: 1,
                fontWeight: 700,
                fontSize: '1rem',
              }}
            >
              {t(item.translationKey)}
            </Typography>
            <Stack 
              spacing={1} 
              sx={{
                pl: 2,
              }}
            >
              {renderItems(item.subItems, level + 1)}
            </Stack>
            {index < items.length - 1 && (
              <Divider sx={{ my: 0.75, height: '2px', opacity: 0.6 }} />
            )}
          </Box>
        );
      }
      
      // Find value in real data if available
      // Only look up the value if isPaid is true, otherwise use 0
      const originalLabel = item.label || item.displayLabel;
      let value = 0;
      
      if (item.calculateValue) {
        const result = item.calculateValue(data);
        value = result.value;
      } else {
        value = isPaid ? findValueByLabel(data, originalLabel) : 0;
      }
      
      return (
        <Box key={index}>
          <Box 
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: { xs: 1, sm: 0 },
              py: 0.75,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="body2" fontWeight="bold" fontSize="1rem">
                {item.displayText ? item.displayText : t(item.translationKey)}
              </Typography>
              {item.translationKey === 'taxReports.summary.Private sale income according to Art. 23 German income tax act' && (
                <ResponsiveTooltip
                  title={
                    <Box sx={{ p: 1.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '0.9rem' }}>
                        {t('taxReports.tooltips.cryptoSales')}
                      </Typography>
                    </Box>
                  }
                  arrow
                  placement="right"
                  componentsProps={{
                    tooltip: {
                      sx: {
                        p: 0,
                        bgcolor: (theme) => theme.palette.background.paper,
                        color: (theme) => theme.palette.text.primary,
                        boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                        border: '1px solid',
                        borderColor: (theme) => theme.palette.divider,
                        borderRadius: 1,
                        fontSize: '0.9rem',
                        fontWeight: 'bold',
                        '& .MuiTooltip-arrow': {
                          color: (theme) => theme.palette.background.paper,
                        }
                      },
                    },
                    popper: {
                      sx: {
                        '& .MuiTooltip-tooltip': {
                          p: 0,
                        }
                      }
                    }
                  }}
                >
                  <InfoOutlinedIcon 
                    sx={{
                      ml: 0.5,
                      fontSize: '1.1rem',
                      color: (theme) => theme.palette.text.secondary,
                      cursor: 'help',
                    }} 
                  />
                </ResponsiveTooltip>
              )}
              {item.translationKey === 'taxReports.summary.Other income according to Art. 22 No. 3 German income tax act' && (
                <ResponsiveTooltip
                  title={
                    <Box sx={{ p: 1.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '0.9rem' }}>
                        {t('taxReports.tooltips.otherIncome')}
                      </Typography>
                    </Box>
                  }
                  arrow
                  placement="right"
                  componentsProps={{
                    tooltip: {
                      sx: {
                        p: 0,
                        bgcolor: (theme) => theme.palette.background.paper,
                        color: (theme) => theme.palette.text.primary,
                        boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                        border: '1px solid',
                        borderColor: (theme) => theme.palette.divider,
                        borderRadius: 1,
                        fontSize: '0.9rem',
                        fontWeight: 'bold',
                        '& .MuiTooltip-arrow': {
                          color: (theme) => theme.palette.background.paper,
                        }
                      },
                    },
                    popper: {
                      sx: {
                        '& .MuiTooltip-tooltip': {
                          p: 0,
                        }
                      }
                    }
                  }}
                >
                  <InfoOutlinedIcon 
                    sx={{
                      ml: 0.5,
                      fontSize: '1.1rem',
                      color: (theme) => theme.palette.text.secondary,
                      cursor: 'help',
                    }} 
                  />
                </ResponsiveTooltip>
              )}
              {item.translationKey === 'taxReports.summary.Capital gains from margin and derivatives' && (
                <ResponsiveTooltip
                  title={
                    <Box sx={{ p: 1.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '0.9rem' }}>
                        {t('taxReports.tooltips.capitalGains')}
                      </Typography>
                    </Box>
                  }
                  arrow
                  placement="right"
                  componentsProps={{
                    tooltip: {
                      sx: {
                        p: 0,
                        bgcolor: (theme) => theme.palette.background.paper,
                        color: (theme) => theme.palette.text.primary,
                        boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                        border: '1px solid',
                        borderColor: (theme) => theme.palette.divider,
                        borderRadius: 1,
                        fontSize: '0.9rem',
                        fontWeight: 'bold',
                        '& .MuiTooltip-arrow': {
                          color: (theme) => theme.palette.background.paper,
                        }
                      },
                    },
                    popper: {
                      sx: {
                        '& .MuiTooltip-tooltip': {
                          p: 0,
                        }
                      }
                    }
                  }}
                >
                  <InfoOutlinedIcon 
                    sx={{
                      ml: 0.5,
                      fontSize: '1.1rem',
                      color: (theme) => theme.palette.text.secondary,
                      cursor: 'help',
                    }} 
                  />
                </ResponsiveTooltip>
              )}
              {item.translationKey === 'taxReports.summary.Private sale income non taxable' && (
                <ResponsiveTooltip
                  title={
                    <Box sx={{ p: 1.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '0.9rem' }}>
                        {t('taxReports.tooltips.taxFreeSales')}
                      </Typography>
                    </Box>
                  }
                  arrow
                  placement="right"
                  componentsProps={{
                    tooltip: {
                      sx: {
                        p: 0,
                        bgcolor: (theme) => theme.palette.background.paper,
                        color: (theme) => theme.palette.text.primary,
                        boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                        border: '1px solid',
                        borderColor: (theme) => theme.palette.divider,
                        borderRadius: 1,
                        fontSize: '0.9rem',
                        fontWeight: 'bold',
                        '& .MuiTooltip-arrow': {
                          color: (theme) => theme.palette.background.paper,
                        }
                      },
                    },
                    popper: {
                      sx: {
                        '& .MuiTooltip-tooltip': {
                          p: 0,
                        }
                      }
                    }
                  }}
                >
                  <InfoOutlinedIcon 
                    sx={{
                      ml: 0.5,
                      fontSize: '1.1rem',
                      color: (theme) => theme.palette.text.secondary,
                      cursor: 'help',
                    }} 
                  />
                </ResponsiveTooltip>
              )}
              {item.translationKey === 'taxReports.summary.Other income non taxable' && (
                <ResponsiveTooltip
                  title={
                    <Box sx={{ p: 1.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '0.9rem' }}>
                        {t('taxReports.tooltips.taxFreeOther')}
                      </Typography>
                    </Box>
                  }
                  arrow
                  placement="right"
                  componentsProps={{
                    tooltip: {
                      sx: {
                        p: 0,
                        bgcolor: (theme) => theme.palette.background.paper,
                        color: (theme) => theme.palette.text.primary,
                        boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                        border: '1px solid',
                        borderColor: (theme) => theme.palette.divider,
                        borderRadius: 1,
                        fontSize: '0.9rem',
                        fontWeight: 'bold',
                        '& .MuiTooltip-arrow': {
                          color: (theme) => theme.palette.background.paper,
                        }
                      },
                    },
                    popper: {
                      sx: {
                        '& .MuiTooltip-tooltip': {
                          p: 0,
                        }
                      }
                    }
                  }}
                >
                  <InfoOutlinedIcon 
                    sx={{
                      ml: 0.5,
                      fontSize: '1.1rem',
                      color: (theme) => theme.palette.text.secondary,
                      cursor: 'help',
                    }} 
                  />
                </ResponsiveTooltip>
              )}
            </Box>
            {!isPaid ? (
              <SkeletonWithTooltip width={80} height={24} />
            ) : (
              <Typography variant="body2" fontWeight="bold" fontSize="1rem">
                {formatAmount(value)}
              </Typography>
            )}
          </Box>
          {index < items.length - 1 && (
            <Divider sx={{ my: 0.75, height: '2px', opacity: 0.6 }} />
          )}
        </Box>
      );
    });
  };

  return (
    <Stack spacing={3}>
      {TAX_REPORT_TEMPLATE.map((section, index) => (
        <TaxSection key={index} section={section} />
      ))}
    </Stack>
  );
};

export default TaxReportSummary;
